import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import {
    Gavel,
    TrendingUp,
    Users,
    FileText,
    BrainCircuit,
    ChevronRight,
    ChevronLeft,
    CheckCircle2,
    Sparkles,
    Loader2,
    Scale,
    Target,
    BarChart3,
    History,
    Calendar,
    Building2,
    DollarSign,
    ShieldCheck,
    AlertTriangle,
    Clock,
    Download,
    Eye,
    Trash2,
    UploadCloud,
    LayoutDashboard,
    RotateCcw,
    Briefcase
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/lib/supabase";
import { UsageTracker } from "@/lib/services/usage-tracker";

// ─── Types ───────────────────────────────────────────────────────────────────

interface PredictionResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    prediction?: {
        probability_of_success: number;
        estimated_duration: {
            months: number;
            display: string;
        };
        risk_level: string;
        estimated_value: string;
    };
    success_probability?: number;
    key_factors?: Array<{
        factor: string;
        importance: string;
        percentage: number;
    }>;
    strategic_recommendations?: Array<{
        title: string;
        priority: string;
        description: string;
    }>;
    risk_assessment?: {
        [key: string]: string;
    };
    similar_cases?: Array<{
        title: string;
        summary: string;
        similarity: string;
        outcome: string;
    }>;
}

interface CaseForm {
    case_type: string;
    jurisdiction: string;
    case_number: string;
    court: string;
    judge_name: string;
    opposing_counsel: string;
    plaintiff_name: string;
    defendant_name: string;
    claim_amount: number;
    case_summary: string;
    key_facts: string[];
    legal_issues: string[];
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .cpv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f3f4f6 100%);
        min-height: 100vh;
      }

      /* ─── Header ─── */
      .cpv2-header {
        background: linear-gradient(135deg, #4c1d95 0%, #5b21b6 40%, #6d28d9 100%);
        padding: 2rem 2.5rem;
        position: relative;
        overflow: hidden;
      }
      .cpv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 80%, rgba(196,181,253,0.15) 0%, transparent 60%);
      }
      .cpv2-header-content { position: relative; z-index: 1; }

      /* ─── Step Indicator ─── */
      .cpv2-step-indicator { display: flex; align-items: center; gap: 0; }
      .cpv2-step {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 700;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        color: rgba(255,255,255,0.5);
      }
      .cpv2-step.active {
        background: rgba(255,255,255,0.15);
        color: #fff;
        backdrop-filter: blur(10px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
      .cpv2-step.completed { color: #c4b5fd; }
      .cpv2-step-connector { width: 1.5rem; height: 1px; background: rgba(255,255,255,0.15); }

      /* ─── Layout ─── */
      .cpv2-body { display: flex; min-height: calc(100vh - 140px); }
      .cpv2-form-panel {
        width: 35%;
        min-width: 360px;
        background: #fff;
        border-right: 1px solid #ddd6fe;
        padding: 2rem;
        overflow-y: auto;
      }
      .cpv2-analytics-panel { flex: 1; padding: 2rem; overflow-y: auto; }

      @media (max-width: 768px) {
        .cpv2-header {
          padding: 1.25rem 1rem !important;
        }
        .cpv2-body {
          flex-direction: column !important;
          min-height: auto !important;
        }
        .cpv2-form-panel {
          width: 100% !important;
          min-width: 100% !important;
          border-right: none !important;
          border-bottom: 1px solid #ddd6fe !important;
          padding: 1.25rem !important;
        }
        .cpv2-analytics-panel {
          width: 100% !important;
          padding: 1.25rem !important;
        }
        .cpv2-step-indicator {
          gap: 0.25rem !important;
        }
        .cpv2-step {
          padding: 0.35rem 0.65rem !important;
          font-size: 0.7rem !important;
        }
        .cpv2-step-connector {
          width: 0.75rem !important;
        }
      }

      /* ─── Field Groups ─── */
      .cpv2-field-group { margin-bottom: 1.5rem; animation: slideUp 0.4s ease-out; }
      @keyframes slideUp { 
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .cpv2-label {
        font-size: 0.7rem;
        font-weight: 900;
        color: #5b21b6;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }

      /* ─── Cards ─── */
      .cpv2-card {
        background: #fff;
        border: 1px solid #eef2ff;
        border-radius: 1.5rem;
        padding: 1.5rem;
        box-shadow: 0 4px 20px -5px rgba(109,40,217,0.05);
      }

      /* ─── Probability Circle ─── */
      .cpv2-prob-ring {
        position: relative;
        width: 140px;
        height: 140px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .cpv2-prob-value {
        font-size: 2.25rem;
        font-weight: 900;
        color: #4c1d95;
      }

      /* ─── Analytics Widgets ─── */
      .cpv2-widget {
        background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
        border: 1px solid #ede9fe;
        border-radius: 1rem;
        padding: 1.25rem;
        transition: all 0.2s;
      }
      .cpv2-widget:hover { border-color: #ddd6fe; transform: scale(1.01); }

      /* ─── Welcome ─── */
      .cpv2-welcome {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;
      }
      .cpv2-welcome-art {
        width: 200px;
        height: 200px;
        background: radial-gradient(circle, #ddd6fe 0%, transparent 70%);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `}</style>
    );
}

// ─── Step Definitions ────────────────────────────────────────────────────────

const STEPS = [
    { id: 1, label: "Fundamentos", icon: FileText },
    { id: 2, label: "Actores", icon: Users },
    { id: 3, label: "Hechos", icon: Gavel },
    { id: 4, label: "Analítica", icon: BrainCircuit },
];

const CASE_TYPES = [
    { value: "Laboral ordinario", label: "Laboral Ordinario" },
    { value: "Tutela", label: "Acción de Tutela" },
    { value: "Civil ordinario", label: "Civil Ordinario" },
    { value: "Administrativo ordinario", label: "Administrativo" },
    { value: "Popular", label: "Acción Popular" },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CasePredictionV2() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startPredictionRun, getRun } = useAgentExecution();
    const isMobile = useIsMobile();
    const [mobileTab, setMobileTab] = useState<"form" | "analytics">("form");

    const [step, setStep] = useState(1);
    const [form, setForm] = useState<CaseForm>({
        case_type: "Laboral ordinario",
        jurisdiction: "Ordinaria",
        case_number: "",
        court: "",
        judge_name: "",
        opposing_counsel: "",
        plaintiff_name: "",
        defendant_name: "",
        claim_amount: 0,
        case_summary: "",
        key_facts: [""],
        legal_issues: [""]
    });

    const predictionRunId = "case-prediction-run";
    const currentRun = getRun(predictionRunId);

    const loading = currentRun?.status === "loading";
    const [localResult, setLocalResult] = useState<PredictionResult | null>(null);
    const result = (currentRun?.metadata as PredictionResult) || localResult;

    // ─── Handlers ──────────────────────────────────────────────────────────

    const addFact = () => setForm(f => ({ ...f, key_facts: [...f.key_facts, ""] }));
    const updateFact = (i: number, val: string) => {
        const newFacts = [...form.key_facts];
        newFacts[i] = val;
        setForm(f => ({ ...f, key_facts: newFacts }));
    };

    const handlePredict = async () => {
        if (!form.case_summary.trim()) {
            toast.error("Por favor, proporcione un resumen del caso.");
            return;
        }

        setLocalResult(null);

        // Track usage for dashboard
        if (user) {
            UsageTracker.trackLitigationPredictor(user.id, "Predicción generada", form.case_type, undefined);
        }

        if (isMobile) {
            setMobileTab("analytics");
        }

        await startPredictionRun(predictionRunId, form, apiClient, user?.id || null);
        setStep(4);
    };

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="cpv2-root">
            <PremiumStyles />

            {/* ── Header ── */}
            <div className="cpv2-header">
                <div className="cpv2-header-content">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20">
                                <TrendingUp className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-black text-white tracking-tighter uppercase italic">Predicción Predicta V2</h1>
                                <p className="text-violet-200 text-[10px] font-bold tracking-widest uppercase opacity-70">Legal Analytics & AI Modeling</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="cpv2-step-indicator">
                                {STEPS.map((s, i) => (
                                    <div key={s.id} className="flex items-center">
                                        <button
                                            onClick={() => { if (s.id < step || (s.id > 1 && form.case_number)) setStep(s.id); }}
                                            className={`cpv2-step ${step === s.id ? "active" : step > s.id ? "completed" : ""}`}
                                        >
                                            {step > s.id ? <CheckCircle2 className="h-3 w-3" /> : <s.icon className="h-3 w-3" />}
                                            <span className="hidden xl:inline">{s.label}</span>
                                        </button>
                                        {i < STEPS.length - 1 && <div className="cpv2-step-connector" />}
                                    </div>
                                ))}
                            </div>
                            <Button variant="ghost" className="text-white hover:bg-white/10 border border-white/10" onClick={() => navigate("/dashboard")}>
                                <LayoutDashboard className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Mobile Tab Switcher ── */}
            {isMobile && (result || loading) && (
                <div className="bg-white border-b border-violet-100 p-2 sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center gap-1 bg-violet-50 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setMobileTab("form")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                mobileTab === "form"
                                    ? "bg-white text-violet-700 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            📝 Caso (Paso {step}/4)
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab("analytics")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                mobileTab === "analytics"
                                    ? "bg-white text-violet-700 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <BrainCircuit className="h-3.5 w-3.5" />
                            <span>Predicción</span>
                            {result?.prediction?.probability_of_success ? (
                                <span className="bg-violet-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-black">
                                    {result.prediction.probability_of_success}%
                                </span>
                            ) : loading ? (
                                <Loader2 className="h-3 w-3 animate-spin text-violet-600" />
                            ) : null}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Body ── */}
            <div className="cpv2-body">
                {/* ── Form Panel ── */}
                {(!isMobile || !result || mobileTab === "form") && (
                <div className="cpv2-form-panel">
                    {step === 1 && (
                        <div className="space-y-6">
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><FileText className="h-4 w-4" /> Tipo de Proceso</div>
                                <Select value={form.case_type} onValueChange={(v) => setForm(f => ({ ...f, case_type: v }))}>
                                    <SelectTrigger className="border-violet-100 h-11 rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Hash className="h-4 w-4" /> Número de Radicado</div>
                                <Input
                                    placeholder="2023-00001"
                                    value={form.case_number}
                                    onChange={(e) => setForm(f => ({ ...f, case_number: e.target.value }))}
                                    className="border-violet-100 h-11 rounded-xl"
                                />
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Building2 className="h-4 w-4" /> Juzgado / Tribunal</div>
                                <Input
                                    placeholder="Juzgado 15 Laboral del Circuito"
                                    value={form.court}
                                    onChange={(e) => setForm(f => ({ ...f, court: e.target.value }))}
                                    className="border-violet-100 h-11 rounded-xl"
                                />
                            </div>
                            <div className="flex justify-end pt-4">
                                <Button onClick={() => setStep(2)} className="bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl px-8 gap-2 h-11 shadow-lg shadow-violet-100">
                                    Siguiente <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Scale className="h-4 w-4" /> Juez Asignado</div>
                                <Input
                                    placeholder="Nombre del Juez"
                                    value={form.judge_name}
                                    onChange={(e) => setForm(f => ({ ...f, judge_name: e.target.value }))}
                                    className="border-violet-100 h-11 rounded-xl"
                                />
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Users className="h-4 w-4" /> Partes del Litigio</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input placeholder="Demandante" value={form.plaintiff_name} onChange={(e) => setForm(f => ({ ...f, plaintiff_name: e.target.value }))} className="border-violet-100 h-10 rounded-lg text-xs" />
                                    <Input placeholder="Demandado" value={form.defendant_name} onChange={(e) => setForm(f => ({ ...f, defendant_name: e.target.value }))} className="border-violet-100 h-10 rounded-lg text-xs" />
                                </div>
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Briefcase className="h-4 w-4" /> Contraparte</div>
                                <Input
                                    placeholder="Abogado o Firma Contraria"
                                    value={form.opposing_counsel}
                                    onChange={(e) => setForm(f => ({ ...f, opposing_counsel: e.target.value }))}
                                    className="border-violet-100 h-11 rounded-xl"
                                />
                            </div>
                            <div className="flex justify-between items-center pt-4">
                                <Button variant="ghost" onClick={() => setStep(1)} className="text-violet-700">Atrás</Button>
                                <Button onClick={() => setStep(3)} className="bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl px-8 h-11 shadow-lg shadow-violet-100">Siguiente</Button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><DollarSign className="h-4 w-4" /> Cuantía Estimada</div>
                                <Input
                                    type="number"
                                    value={form.claim_amount}
                                    onChange={(e) => setForm(f => ({ ...f, claim_amount: Number(e.target.value) }))}
                                    className="border-violet-100 h-11 rounded-xl"
                                />
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><FileText className="h-4 w-4" /> Resumen Fáctico</div>
                                <Textarea
                                    value={form.case_summary}
                                    onChange={(e) => setForm(f => ({ ...f, case_summary: e.target.value }))}
                                    placeholder="Describa brevemente los hechos del caso..."
                                    className="border-violet-100 min-h-[120px] rounded-xl text-sm"
                                />
                            </div>
                            <div className="cpv2-field-group">
                                <div className="cpv2-label"><Target className="h-4 w-4" /> Hechos Relevantes</div>
                                <div className="space-y-2">
                                    {form.key_facts.map((fact, i) => (
                                        <Input
                                            key={i}
                                            value={fact}
                                            onChange={(e) => updateFact(i, e.target.value)}
                                            placeholder={`Hecho #${i + 1}`}
                                            className="border-violet-100 h-9 rounded-lg text-xs"
                                        />
                                    ))}
                                    <Button variant="ghost" onClick={addFact} className="text-violet-600 p-0 h-6 text-[10px] font-black uppercase">+ Agregar Hecho</Button>
                                </div>
                            </div>
                            <Button
                                className="w-full h-12 bg-violet-900 hover:bg-violet-800 text-white font-black rounded-xl gap-2 shadow-xl shadow-violet-200 mt-4"
                                onClick={handlePredict}
                                disabled={loading}
                            >
                                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <BrainCircuit className="h-5 w-5" />}
                                Generar Predicción AI
                            </Button>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-6">
                            <div className="cpv2-card bg-violet-50/50 border-violet-100">
                                <div className="flex flex-col items-center text-center py-4">
                                    {loading ? (
                                        <>
                                            <div className="w-16 h-16 rounded-3xl bg-white shadow-inner flex items-center justify-center mb-4">
                                                <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
                                            </div>
                                            <h3 className="text-sm font-black text-violet-900 uppercase">Modelando Resultados</h3>
                                            <p className="text-[10px] text-violet-500 mt-1 font-bold">Contrastando precedentes y jurisprudencia...</p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-16 h-16 rounded-full bg-violet-600 flex items-center justify-center mb-4 shadow-lg shadow-violet-200">
                                                <CheckCircle2 className="h-8 w-8 text-white" />
                                            </div>
                                            <h3 className="text-sm font-black text-violet-900 uppercase">Análisis Finalizado</h3>
                                            <p className="text-[10px] text-violet-500 mt-1 font-bold">Probabilidad de éxito estimada: {result?.prediction?.probability_of_success || result?.success_probability || 0}%</p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {currentRun?.traceSteps && (
                                <div className="space-y-3 px-2">
                                    {currentRun.traceSteps.map((s, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-lg">{s.emoji}</span>
                                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">{s.message}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!loading && (
                                <Button
                                    variant="outline"
                                    className="w-full border-violet-200 text-violet-700 rounded-xl h-11 mt-4 font-bold"
                                    onClick={() => {
                                        setStep(1);
                                        setMobileTab("form");
                                        setLocalResult(null);
                                    }}
                                >
                                    <RotateCcw className="h-4 w-4 mr-2" /> Nuevo Análisis
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* ── Analytics Panel ── */}
                {(!isMobile || ((result || loading) && mobileTab === "analytics")) && (
                <div className="cpv2-analytics-panel">
                    {!result && !loading ? (
                        <div className="cpv2-welcome">
                            <div className="cpv2-welcome-art">
                                <BrainCircuit className="h-16 w-16 text-violet-400 opacity-50" />
                            </div>
                            <h2 className="text-2xl font-black text-violet-900 mt-6 tracking-tight">Legal Analytics Engine</h2>
                            <p className="text-slate-400 mt-2 max-w-sm font-medium text-sm">
                                Utilice nuestro motor predictivo para estimar probabilidades de éxito basadas en datos históricos.
                            </p>
                        </div>
                    ) : result?.status === 'rejected' ? (
                        <div className="max-w-2xl mx-auto py-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="cpv2-card border-t-4 border-t-red-500 shadow-2xl shadow-red-100/50 p-8">
                                <div className="flex items-start gap-6 mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0 shadow-inner">
                                        <BrainCircuit className="h-9 w-9" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-2">Simulación Cancelada</h3>
                                        <p className="text-slate-500 leading-relaxed font-medium">
                                            Nuestros sistemas de IA han determinado que los datos proporcionados no contienen los elementos judiciales mínimos para una predicción válida.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4 p-6 bg-slate-50 rounded-2xl border border-slate-100 mb-8">
                                    <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Criterio de Validación</div>
                                        <div className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-black rounded-full uppercase">Insuficiente</div>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-600 font-bold text-sm">Confianza de Análisis</span>
                                        <span className="text-slate-900 font-black text-sm">{Math.round((result.guard_rail_details?.confidence || 0) * 100)}%</span>
                                    </div>

                                    <div className="pt-2">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Motivo Técnico</div>
                                        <p className="text-xs text-slate-700 italic font-medium leading-relaxed bg-white p-4 rounded-xl border border-slate-200">
                                            "{result.guard_rail_details?.reason || result.message}"
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <Button
                                        variant="outline"
                                        className="h-12 rounded-xl border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all"
                                        onClick={() => { setStep(1); setLocalResult(null); }}
                                    >
                                        <RotateCcw className="h-4 w-4 mr-2" /> REINTENTAR
                                    </Button>
                                    <Button
                                        className="h-12 rounded-xl bg-violet-900 text-white font-bold hover:bg-violet-800 shadow-lg shadow-violet-200 transition-all"
                                        onClick={() => navigate('/dashboard')}
                                    >
                                        VOLVER AL PANEL
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Probability Widget */}
                                <div className="lg:col-span-1 cpv2-card flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-white to-violet-50/50">
                                    <div className="cpv2-label mb-4">Probabilidad de Éxito</div>
                                    <div className="cpv2-prob-ring">
                                        <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                            <circle cx="70" cy="70" r="60" stroke="#f3f4f6" strokeWidth="12" fill="none" />
                                            <circle
                                                cx="70" cy="70" r="60" stroke="#6d28d9" strokeWidth="12" fill="none"
                                                strokeDasharray="377"
                                                strokeDashoffset={377 - (377 * (result?.prediction?.probability_of_success || result?.success_probability || 0) / 100)}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="cpv2-prob-value">{result?.prediction?.probability_of_success || result?.success_probability || 0}%</span>
                                    </div>
                                    <div className="mt-6 flex gap-2">
                                        <span className="px-3 py-1 bg-violet-100 text-violet-700 text-[10px] font-black rounded-lg uppercase">
                                            Riesgo: {result?.prediction?.risk_level || "Bajo"}
                                        </span>
                                    </div>
                                </div>

                                {/* Key Insights */}
                                <div className="lg:col-span-2 space-y-4">
                                    <div className="flex items-center gap-2 px-2">
                                        <Target className="h-5 w-5 text-violet-600" />
                                        <h3 className="font-black text-violet-900 text-xs uppercase italic">Factores Determinantes</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {result?.key_factors?.map((f, i) => (
                                            <div key={i} className="cpv2-widget">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-[10px] font-bold text-slate-800 uppercase">{f.factor}</span>
                                                    <span className="text-[10px] font-black text-violet-600">{f.percentage}%</span>
                                                </div>
                                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-violet-500" style={{ width: `${f.percentage}%` }} />
                                                </div>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-4">Analizando factores de impacto...</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Strategy Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 px-2">
                                        <BrainCircuit className="h-5 w-5 text-violet-600" />
                                        <h3 className="font-black text-violet-900 text-xs uppercase italic">Hoja de Ruta Estratégica</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {result?.strategic_recommendations?.map((r, i) => (
                                            <div key={i} className="cpv2-card p-4 border-l-4 border-l-violet-600">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`w-2 h-2 rounded-full ${r.priority === 'high' ? 'bg-red-500' : 'bg-orange-400'}`} />
                                                    <h4 className="font-bold text-slate-900 text-sm">{r.title}</h4>
                                                </div>
                                                <p className="text-xs text-slate-500 leading-normal">{r.description}</p>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-4 font-medium italic">Calculando recomendaciones estratégicas óptimas...</p>}
                                    </div>
                                </div>

                                {/* Precedents Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 px-2">
                                        <History className="h-5 w-5 text-violet-600" />
                                        <h3 className="font-black text-violet-900 text-xs uppercase italic">Precedentes Similares</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {result?.similar_cases?.map((c, i) => (
                                            <div key={i} className="cpv2-widget flex items-start gap-4">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.outcome === 'success' ? 'bg-green-100' : 'bg-amber-100'}`}>
                                                    <Scale className={`h-4 w-4 ${c.outcome === 'success' ? 'text-green-600' : 'text-amber-600'}`} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex justify-between mb-1">
                                                        <span className="font-bold text-slate-900 text-xs">{c.title}</span>
                                                        <span className="text-[10px] font-black text-violet-600">{c.similarity}% SIM.</span>
                                                    </div>
                                                    <p className="text-[10px] text-slate-500 line-clamp-2">{c.summary}</p>
                                                </div>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-4 font-medium italic">Consultando base de datos de jurisprudencia alta corte...</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>
            <style>{`
                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @keyframes slideUp { 
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

function Hash(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="4" x2="20" y1="9" y2="9" />
            <line x1="4" x2="20" y1="15" y2="15" />
            <line x1="10" x2="8" y1="3" y2="21" />
            <line x1="16" x2="14" y1="3" y2="21" />
        </svg>
    )
}
